import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCheckoutStore } from '@stores/checkoutStore';
import { getAffiliateProductByCode } from '@src/services/affiliate';
import { toast } from 'react-toastify';

/**
 * Hook to handle affiliate product data fetching and validation
 * Used in each step of the affiliate checkout flow
 */
export const useAffiliateProduct = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    affiliateProductCode,
    affiliateProductData,
    affiliateRef,
    setAffiliateRef,
    setAffiliateProductCode,
    setAffiliateProductData,
  } = useCheckoutStore();

  useEffect(() => {
    // Check if we need to extract params from URL
    const refParam = searchParams.get('ref');
    const productParam = searchParams.get('product');

    // Store ref if present in URL
    if (refParam && refParam !== affiliateRef) {
      setAffiliateRef(refParam);
    }

    // Store product code if present in URL
    if (productParam && productParam !== affiliateProductCode) {
      setAffiliateProductCode(productParam);
    }
  }, [
    searchParams,
    affiliateRef,
    affiliateProductCode,
    setAffiliateRef,
    setAffiliateProductCode,
  ]);

  useEffect(() => {
    // If we don't have product code in store, redirect to home
    if (!affiliateProductCode) {
      toast.error('Invalid affiliate link. Product code is missing.');
      navigate('/');

      return;
    }

    // If we already have product data, no need to fetch again
    if (affiliateProductData) {
      return;
    }

    // Fetch product data
    const fetchProductData = async () => {
      try {
        const response = await getAffiliateProductByCode(affiliateProductCode);

        setAffiliateProductData(response.data);
      } catch (error: any) {
        console.error('Failed to fetch affiliate product:', error);
        toast.error(
          error?.response?.data?.detail ||
            'Failed to load product information. Please check your affiliate link.'
        );
        navigate('/');
      }
    };

    fetchProductData();
  }, [
    affiliateProductCode,
    affiliateProductData,
    setAffiliateProductData,
    navigate,
  ]);

  const buildAffiliateUrl = (path: string) => {
    const params = new URLSearchParams();

    if (affiliateRef) params.set('ref', affiliateRef);
    if (affiliateProductCode) params.set('product', affiliateProductCode);

    return `${path}?${params.toString()}`;
  };

  return {
    affiliateProductCode,
    affiliateProductData,
    affiliateRef,
    buildAffiliateUrl,
    isLoading: !affiliateProductData && !!affiliateProductCode,
  };
};
